import type { AppRouteRecordRaw, Menu } from '/@/router/types'

import { defineStore } from 'pinia'
import { store } from '/@/store'
import { ref, computed } from 'vue'
// import { useI18n } from '/@/hooks/web/useI18n'
import { useUserStore } from './user'
import { useAppStoreWithOut } from './app'
import { transformObjToRoute, flatMultiLevelRoutes } from '/@/router/helper/routeHelper'

import projectSetting from '/@/settings/projectSetting'

import { PermissionModeEnum } from '/@/enums/appEnum'

import { PAGE_NOT_FOUND_ROUTE } from '/@/router/routes/basic'

import { filter } from '/@/utils/helper/treeHelper'

// import { getPermCode } from '/@/api/sys/user'

// import { useMessage } from '/@/hooks/web/useMessage'
import { PageEnum } from '/@/enums/pageEnum'

import { dynamicRoutes } from '/@/router/routes/dynamic'

export const usePermissionStore = defineStore('app-permission', () => {
  // State
  const permCodeList = ref<string[] | number[]>([])
  const isDynamicAddedRoute = ref(false)
  const lastBuildMenuTime = ref(0)
  const backMenuList = ref<Menu[]>([])
  const systemManageMenuList = ref<Menu[]>([])
  const frontMenuList = ref<Menu[]>([])

  // Getters
  const getPermCodeList = computed(() => permCodeList.value)
  const getBackMenuList = computed(() => backMenuList.value)
  const getSystemManageMenuList = computed(() => systemManageMenuList.value)
  const getFrontMenuList = computed(() => frontMenuList.value)
  const getLastBuildMenuTime = computed(() => lastBuildMenuTime.value)
  const getIsDynamicAddedRoute = computed(() => isDynamicAddedRoute.value)

  // Actions
  function setPermCodeList(codeList: string[]) {
    permCodeList.value = codeList
  }

  function setBackMenuList(list: Menu[]) {
    backMenuList.value = list
    list?.length > 0 && setLastBuildMenuTime()
  }

  function setSystemManageMenuList(list: Menu[]) {
    systemManageMenuList.value = list
  }

  function setFrontMenuList(list: Menu[]) {
    frontMenuList.value = list
  }

  function setLastBuildMenuTime() {
    lastBuildMenuTime.value = new Date().getTime()
  }

  function setDynamicAddedRoute(added: boolean) {
    isDynamicAddedRoute.value = added
  }

  function resetState(): void {
    isDynamicAddedRoute.value = false
    permCodeList.value = []
    backMenuList.value = []
    lastBuildMenuTime.value = 0
  }

  async function changePermissionCode() {
    // const codeList = await getPermCode()
    // setPermCodeList(codeList)
  }

  async function buildRoutesAction(): Promise<{
    baseRouteList: AppRouteRecordRaw[]
    routes: AppRouteRecordRaw[]
  }> {
    // const { t } = useI18n()
    const userStore = useUserStore()
    const appStore = useAppStoreWithOut()

    let routes: AppRouteRecordRaw[] = []
    const { permissionMode = projectSetting.permissionMode } = appStore.getProjectConfig

    const routeRemoveIgnoreFilter = (route: AppRouteRecordRaw) => {
      const { meta } = route
      const { ignoreRoute } = meta || {}
      return !ignoreRoute
    }

    /**
     * @description 根据设置的首页path，修正routes中的affix标记（固定首页）
     * */
    const patchHomeAffix = (routes: AppRouteRecordRaw[]) => {
      if (!routes || routes.length === 0) return
      let homePath: string = userStore.getUserInfo.homePath || PageEnum.BASE_HOME
      function patcher(routes: AppRouteRecordRaw[], parentPath = '') {
        if (parentPath) parentPath = parentPath + '/'
        routes.forEach((route: AppRouteRecordRaw) => {
          const { path, children, redirect } = route
          const currentPath = path.startsWith('/') ? path : parentPath + path
          if (currentPath === homePath) {
            if (redirect) {
              homePath = route.redirect! as string
            } else {
              route.meta = Object.assign({}, route.meta, { affix: true })
              throw new Error('end')
            }
          }
          children && children.length > 0 && patcher(children, currentPath)
        })
      }
      try {
        patcher(routes)
      } catch (e) {
        // 已处理完毕跳出循环
      }
      return
    }
    let routeList: AppRouteRecordRaw[] = []
    switch (permissionMode) {
      case PermissionModeEnum.BACK:
        // const { createMessage } = useMessage()

        // createMessage.loading({
        //   content: t('sys.app.menuLoading'),
        //   duration: 1,
        // })

        // !Simulate to obtain permission codes from the background,
        // this function may only need to be executed once, and the actual project can be put at the right time by itself

        try {
          changePermissionCode()
          // routeList = (await getMenuList()) as AppRouteRecordRaw[]
          routeList = dynamicRoutes as AppRouteRecordRaw[]
        } catch (error) {
          console.error(error)
        }

        // Dynamically introduce components
        routeList = transformObjToRoute(routeList)
        // remove meta.ignoreRoute item
        routeList = filter(routeList, routeRemoveIgnoreFilter)
        routeList = routeList.filter(routeRemoveIgnoreFilter)
        routeList = flatMultiLevelRoutes(routeList)
        routes = [PAGE_NOT_FOUND_ROUTE, ...routeList]
        break
    }

    patchHomeAffix(routes)
    return {
      baseRouteList: routeList,
      routes,
    }
  }

  return {
    permCodeList,
    isDynamicAddedRoute,
    lastBuildMenuTime,
    backMenuList,
    systemManageMenuList,
    frontMenuList,
    getPermCodeList,
    getBackMenuList,
    getSystemManageMenuList,
    getFrontMenuList,
    getLastBuildMenuTime,
    getIsDynamicAddedRoute,
    setPermCodeList,
    setBackMenuList,
    setSystemManageMenuList,
    setFrontMenuList,
    setLastBuildMenuTime,
    setDynamicAddedRoute,
    resetState,
    changePermissionCode,
    buildRoutesAction,
  }
})

// Need to be used outside the setup
export function usePermissionStoreWithOut() {
  return usePermissionStore(store)
}
